import AppLoader from '@nxsys/components/AppLoader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import { useSubmitPensionContributionReport } from '@nxsys/hooks/api/report';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetPendingPensionContributionReportListParams } from '@nxsys/store/slices/report/types';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { debounce } from 'lodash';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PensionContributionFilterModal, {
  PensionContributionFilterValues,
} from '../components/PensionContributionFilterModal';
import { getPendingPensionContributionReportColumns } from './columns';
import { PensionContributionTabRef } from './PensionContributionReport';
import ApprovePaymentModal from '../components/ApprovePaymentModal';
import { APP_NAME_BUREAU } from '@nxsys/store/constants/keys';

export type PendingPensionContributionTabProps = {
  pensionContributionReportRef?: React.RefObject<PensionContributionTabRef>;
};

const PendingPensionContributionTab: React.FC<
  PendingPensionContributionTabProps
> = ({ pensionContributionReportRef }) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch();
  const { pendingPensionContributionReportList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } =
    pendingPensionContributionReportList;
  const { payrollCountry, isBureauEmployer, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IPensionSubmission[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const [isOpenApprovePaymentModal, setIsOpenApprovePaymentModal] =
    useState(false);

  // Companies on manual pension mode must export (not submit/approve via API).
  const hasManualSelected = () =>
    selectedRows.some(
      (row: any) => row?.company?.autoEnrolSettings?.managePensionManually,
    );

  const warnManualMode = () => {
    showMessage({
      type: 'warning',
      content:
        messages['report.pension.manualModeExportInstead'] ??
        'One or more selected companies manage pension manually. Submission is disabled — please use Export instead.',
    });
  };

  useImperativeHandle(pensionContributionReportRef, () => ({
    submitPensionFromPayroll: submitPensionByPayrollSelected,
    approvePayment: () => {
      if (hasManualSelected()) {
        warnManualMode();
        return;
      }
      setIsOpenApprovePaymentModal(true);
    },
  }));

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, []);

  const handleFetchData = (
    params: GetPendingPensionContributionReportListParams,
  ) => {
    const newParams = {
      ...params,
      reportType: 'pension',
      isPendingSubmitPension: true,
      paymentModels: isBureauEmployer
        ? [PAYMENT_MODEL_ENUM.PAYEE]
        : [
            PAYMENT_MODEL_ENUM.UMBRELLA,
            PAYMENT_MODEL_ENUM.PAYEE,
            PAYMENT_MODEL_ENUM.HYBRID,
            PAYMENT_MODEL_ENUM.PAYE_HYBRID,
            PAYMENT_MODEL_ENUM.PAYE_FTH,
          ],
      companyRefs: isBureauEmployer ? user?.companyUserIds : undefined,
      appName: isBureau ? APP_NAME_BUREAU : undefined,
    };

    dispatch(reportActions.getPendingPensionContributionReportList(newParams));
  };

  const refetch = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: PensionContributionFilterValues) => {
    const params: GetPendingPensionContributionReportListParams = {
      ...queryParams,
      payFrequency: values.payFrequency,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
    };
    handleFetchData(params);
  };

  const {
    run: submitPensionContributionReport,
    loading: submitPensionContributionReportLoading,
  } = useSubmitPensionContributionReport();
  const submitPensionByPayrollSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    if (hasManualSelected()) {
      warnManualMode();
      return;
    }
    submitPensionContributionReport({
      ids: selectedRows.map((item) => item.id),
    }).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content:
            messages['report.pension.submitPensionContributionReportSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content:
            res.message ||
            messages['report.pension.submitPensionContributionReportError'],
        });
      }
      refetch();
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.payFrequency;

  return (
    <>
      {submitPensionContributionReportLoading && <AppLoader />}
      <AppsHeader>
        <div className="flex justify-between flex-wrap w-full gap-4">
          <div className="flex flex-wrap gap-4">
            <AppInputSearch onChange={handleSearch} />
            <AppMenuFilter
              options={[]}
              onChange={() => {}}
              onClick={() => setIsOpenFilterModal(true)}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <TaxYearSelect
              initValue={currentTaxYear}
              onFilter={(value) =>
                handleFetchData({ ...queryParams, taxYear: value.taxYear })
              }
            />
          </div>
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2700, y: 700 }}
          columns={getPendingPensionContributionReportColumns({
            messages,
            payrollCountry,
            isBureau,
          })}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>
      <PensionContributionFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        isBureau={isBureau}
      />
      <ApprovePaymentModal
        isOpen={isOpenApprovePaymentModal}
        toggle={() => setIsOpenApprovePaymentModal(false)}
      />
    </>
  );
};

export default PendingPensionContributionTab;
